import React, { useState } from 'react';
import {
    useTable,
    useGlobalFilter,
    useAsyncDebounce,
    useRowSelect,
    useFilters,
    useSortBy,
} from 'react-table';
import IconComponent from '../../Icon';
import { Checkbox, Input, LoadingSpinner, RowContainer, Typography } from '..';

function GlobalFilter({ globalFilter, setGlobalFilter, placeholder = '', controls, isLoading = false }) {
    const [value, setValue] = useState(globalFilter);
    const onChangeInternal = useAsyncDebounce((value) => {
        setGlobalFilter(value || undefined);
    }, 200);

    const onChange = (e) => {
        setValue(e.target.value);
        onChangeInternal(e.target.value);
    };

    return (
        <RowContainer spacing="header" className="mb-6">
            <div className="flex-grow">
                <div className="flex gap-6">
                    <div className="w-3/6">
                        <Input
                            disabled={isLoading}
                            type="search"
                            value={value || ''}
                            className="w-full"
                            onChange={onChange}
                            placeholder={placeholder ? placeholder : `Search...`}
                        />
                    </div>
                    {controls ? controls : <></>}
                </div>
            </div>
        </RowContainer>
    );
}

export const Table = ({
    columns,
    hiddenColumns = columns.filter((c) => c.hidden).map((c) => c.accessor),
    data,
    actions,
    selectable,
    controls,
    filterPlaceholder,
    filterable,
    getRowProps,
    isLoading,
    loadingElement
}) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        globalFilter,
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data,
            initialState: {
                hiddenColumns,
            },
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) =>
                [
                    {
                        id: 'selection',
                        Header: () => <></>,
                        hidden: !selectable,
                        Cell: ({ row }) => (
                            <div>
                                <Checkbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        ),
                    },
                    ...columns,
                    {
                        id: 'actions',
                        Header: () => <></>,
                        Cell: ({ row }) => (actions ? actions(row) : <></>),
                    },
                ].filter((i) => !i.hidden)
            );
        }
    );

    return (
        <>
            {filterable ? (
                <GlobalFilter
                    isLoading={isLoading}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    placeholder={filterPlaceholder}
                    controls={controls}
                />
            ) : (
                <></>
            )}
            <table className="table-auto w-full" {...getTableProps()}>
                <thead className="bg-grey-light rounded-t-lg">
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column, index) => (
                                <th
                                    className={`text-left px-6 py-3 font-PoppinsRegular tracking-wide text-14/20 uppercase text-grey-normal ${
                                        index === 0
                                            ? 'rounded-tl-lg'
                                            : index === headerGroup.headers.length - 1
                                            ? 'rounded-tr-lg'
                                            : ''
                                    }`}
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                >
                                    <div className="flex items-center">
                                        {column.render('Header')}
                                        <span className="ml-3">
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <IconComponent
                                                        width="12"
                                                        height="12"
                                                        icon="chevron"
                                                    />
                                                ) : (
                                                    <IconComponent
                                                        width="12"
                                                        height="12"
                                                        icon="chevron"
                                                        rotate="180"
                                                    />
                                                )
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody className="bg-white rounded-b-xl" {...getTableBodyProps()}>
                    {isLoading ? <tr><td className="p-2" colSpan={headerGroups[0].headers.length}>{loadingElement ?? <LoadingSpinner />}</td></tr> : <></>}
                    {rows.map((row, i) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps(getRowProps && getRowProps(row))}>
                                {row.cells.map((cell, cellIndex) => {
                                    return (
                                        <td
                                            className={`px-6 py-3 ${
                                                i === rows.length - 1
                                                    ? cellIndex === 0
                                                        ? 'rounded-bl-lg'
                                                        : cellIndex === row.cells.length - 1
                                                        ? 'rounded-br-lg'
                                                        : ''
                                                    : ''
                                            }`}
                                            {...cell.getCellProps()}
                                        >
                                            <Typography variant="body1">
                                                {cell.render('Cell')}
                                            </Typography>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
};
